/**
 * /refund — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * Real policy, matching how billing actually works: monthly in advance, thirty
 * days' notice, service credits for missed availability. Written plainly
 * because a refund policy is read by someone who is already unhappy, and
 * hedged language at that moment reads as evasion.
 */
import { LegalPage } from '../components/LegalPage';

export default function RefundPage() {
  return (
    <LegalPage
      title="Refund policy"
      description="When Bloomneo refunds and when it does not: billing errors in full, service credits for missed uptime, no refund for a notice period already served."
      updated="20 September 2026"
      draft={false}
      intro={<>Plans are billed monthly in advance by Krishna Teja GS, who operates Bloomneo. This page says what happens to that money in the situations where someone asks for it back.</>}
      sections={[
        {
          /*
            A page called "Refund policy" that never said when a refund is
            given. It covered billing errors, service credits and notice
            periods — every case EXCEPT somebody who paid, tried it, and
            wanted their money back. A payment reviewer looks for exactly that
            rule, and so does a customer deciding whether to risk the first
            fifty dollars.
          */
          heading: 'First payment',
          body: (
            <p>
              If Bloomneo is not what you expected, tell us within <strong>7 days of your first
              payment</strong> and we will refund it in full. One refund per customer, on the first
              month only — after that the terms below apply. You do not need a reason, and nothing
              is deleted the moment you ask: your applications keep running through the notice
              period so you have time to move them.
            </p>
          ),
        },
        {
          heading: 'The short version',
          body: (
            <p>
              We do not refund the unused part of a month you have already paid for, because the
              service ran for it. We do refund anything we billed in error, and we issue service
              credits when we miss the availability we committed to.
            </p>
          ),
        },
        {
          heading: 'Cancellation',
          body: (
            <p>
              Either side may end the arrangement with 30 days' notice. The service continues
              through the notice period and is charged for it, which is what pays for the handover
              — returning DNS, servers and backups in a state you can carry on with. The{' '}
              <a href="/cancellation" className="text-primary underline-offset-4 hover:underline">cancellation policy</a>{' '}
              sets out that process. No refund is due for the notice period itself.
            </p>
          ),
        },
        {
          heading: 'Billing errors',
          body: (
            <p>
              If we charged you for something you did not have, charged twice, or billed the wrong
              amount, tell us and we refund it in full. We do not treat our own mistakes as a
              matter of goodwill.
            </p>
          ),
        },
        {
          heading: 'Missed availability',
          body: (
            <p>
              When monthly uptime falls below the target in your plan, the remedy is a service
              credit against the following month, calculated as set out in the{' '}
              <a href="/sla" className="text-primary underline-offset-4 hover:underline">service level agreement</a>
              . Credits are applied to the account rather than paid out.
            </p>
          ),
        },
        {
          heading: 'Pass-through costs',
          body: (
            <p>
              Domain registrations, certificates purchased on your behalf and one-off infrastructure
              we bought at your request are not refundable once paid to the provider — a registered
              domain is a year of somebody else's fee, and it stays registered to you.
            </p>
          ),
        },
        {
          heading: 'How to ask',
          body: (
            <p>
              Email{' '}
              <a href="mailto:support@bloomneo.com" className="text-primary underline-offset-4 hover:underline">support@bloomneo.com</a>{' '}
              with the invoice and what went wrong. We respond within five working days, and
              approved refunds are returned by the method you paid with, usually within ten.
            </p>
          ),
        },
      ]}
    />
  );
}
